import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PaginationBar, usePagination } from "@/components/Pagination";
import { MapPin, LogIn, LogOut, Navigation, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  on_time: "text-emerald-500", late: "text-yellow-500", out_of_range: "text-destructive",
};
const STATUS_LABEL = { on_time: "On Time", late: "Late", out_of_range: "Out of Range" };

async function getPosition() {
  if (!navigator.geolocation) throw new Error("Your device does not support GPS. Please enable location services.");
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      p => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      err => {
        if (err.code === 1) reject(new Error("Location permission denied — please enable location access for this site and try again."));
        else if (err.code === 2) reject(new Error("Location unavailable — move to an area with better signal and try again."));
        else reject(new Error("Could not get location — please try again."));
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  });
}

async function reverseGeocode(lat, lng) {
  try {
    const r = await fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&zoom=16`,
      { headers: { Accept: "application/json" } });
    const d = await r.json();
    return d.display_name || "";
  } catch { return ""; }
}

export default function GeoAttendance() {
  const [today, setToday] = useState(null);
  const [filters, setFilters] = useState({ date_from: "", date_to: "", employee_id: "", status: "", establishment_id: "" });
  const [rows, setRows] = useState([]);
  const [emps, setEmps] = useState([]);
  const [working, setWorking] = useState(false);
  const pg = usePagination(rows);

  const loadToday = useCallback(async () => {
    try { const { data } = await api.get("/attendance/today"); setToday(data); }
    catch (e) { toast.error(formatErr(e)); }
  }, []);
  const loadList = useCallback(async () => {
    try {
      const p = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
      const [l, e] = await Promise.all([api.get(`/attendance/entries?${p}`), api.get("/employees")]);
      setRows(l.data.rows || []); setEmps(e.data.filter(x => x.status !== "Inactive"));
    } catch (e) { toast.error(formatErr(e)); }
  }, [filters]);
  useEffect(() => { loadToday(); }, [loadToday]);
  useEffect(() => { loadList(); }, [loadList]);

  const mark = async (kind) => {
    setWorking(true);
    try {
      const { lat, lng } = await getPosition();
      const address = await reverseGeocode(lat, lng);
      await api.post("/attendance/mark", { lat, lng, address, kind });
      toast.success(kind === "check_in" ? "Checked in" : "Checked out");
      await loadToday(); await loadList();
    } catch (e) { toast.error(e.message || formatErr(e)); }
    finally { setWorking(false); }
  };

  const fence = today?.geo_fence;
  const t = today?.entry;
  const checkInDone = !!t?.check_in;
  const checkOutDone = !!t?.check_out;
  const locLink = (lat, lng) => `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  const addrOrCoords = (s) => s?.address || (s ? `${s.lat.toFixed(5)}, ${s.lng.toFixed(5)}` : "—");

  return (
    <div className="p-8">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><MapPin className="w-3 h-3"/> HRMS · Attendance</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Geo Location Attendance</h1>
          <div className="text-xs text-muted-foreground mt-1">GPS-verified check-in / check-out with establishment geo-fence.</div>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="geo-checkin-btn" disabled={working || checkInDone || !today?.linked} onClick={() => mark("check_in")} className="rounded-sm text-xs uppercase tracking-wider">
            <LogIn className="w-3 h-3 mr-1"/>{working ? "Locating…" : checkInDone ? "Checked In" : "Mark Check-In"}
          </Button>
          <Button data-testid="geo-checkout-btn" disabled={working || !checkInDone || checkOutDone} onClick={() => mark("check_out")} variant="outline" className="rounded-sm text-xs uppercase tracking-wider">
            <LogOut className="w-3 h-3 mr-1"/>{checkOutDone ? "Checked Out" : "Mark Check-Out"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6" data-testid="geo-today-cards">
        <div className="border border-border bg-card rounded-sm p-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Today's Status</div>
          {!today?.linked && <div className="text-xs text-destructive flex items-center gap-1" data-testid="geo-not-linked"><AlertCircle className="w-3.5 h-3.5"/> No employee record linked to your user</div>}
          {today?.linked && !t && <div className="text-xs text-muted-foreground">Not yet marked</div>}
          {t && <div className="text-sm font-display font-black uppercase"><span className={STATUS_COLORS[t.status] || ""}>{STATUS_LABEL[t.status] || t.status}</span><span className="text-xs text-muted-foreground ml-2 font-mono">({Math.round(t.distance_m || 0)}m from base)</span></div>}
        </div>
        <div className="border border-border bg-card rounded-sm p-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Check-In</div>
          <div className="text-sm font-mono" data-testid="geo-today-checkin">{t?.check_in ? new Date(t.check_in.time).toLocaleTimeString() : "—"}</div>
          {t?.check_in && <a data-testid="geo-today-checkin-map" href={locLink(t.check_in.lat, t.check_in.lng)} target="_blank" rel="noreferrer" className="text-[10px] text-primary hover:underline uppercase tracking-wider">View on Map</a>}
        </div>
        <div className="border border-border bg-card rounded-sm p-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Check-Out</div>
          <div className="text-sm font-mono" data-testid="geo-today-checkout">{t?.check_out ? new Date(t.check_out.time).toLocaleTimeString() : "—"}</div>
          {t?.check_out && <a href={locLink(t.check_out.lat, t.check_out.lng)} target="_blank" rel="noreferrer" className="text-[10px] text-primary hover:underline uppercase tracking-wider">View on Map</a>}
        </div>
      </div>

      {fence && (
        <div className="border border-primary/30 bg-primary/5 rounded-sm p-3 mb-4 flex items-center justify-between text-xs" data-testid="geo-fence-info">
          <span><Navigation className="w-3 h-3 inline mr-1"/> Geo-fence: {fence.radius_m}m around {fence.base_lat.toFixed(4)}, {fence.base_lng.toFixed(4)} · Mode: <span className="font-semibold uppercase">{fence.mode}</span> · Cutoff: {fence.on_time_cutoff}</span>
          {fence.enabled === false && <span className="text-muted-foreground">(disabled)</span>}
        </div>
      )}

      <div className="flex items-end gap-2 flex-wrap mb-4 border border-border bg-card rounded-sm p-3" data-testid="geo-filter-bar">
        <div className="w-[160px]"><Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Employee</Label>
          <Select value={filters.employee_id || "all"} onValueChange={(v) => setFilters(f => ({ ...f, employee_id: v === "all" ? "" : v }))}>
            <SelectTrigger data-testid="geo-filter-emp" className="mt-1 rounded-sm bg-secondary border-border h-8 text-xs"><SelectValue/></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem>{emps.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="w-[140px]"><Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">From</Label>
          <Input data-testid="geo-filter-from" type="date" value={filters.date_from} onChange={(e) => setFilters(f => ({ ...f, date_from: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border h-8 text-xs"/>
        </div>
        <div className="w-[140px]"><Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">To</Label>
          <Input data-testid="geo-filter-to" type="date" value={filters.date_to} onChange={(e) => setFilters(f => ({ ...f, date_to: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border h-8 text-xs"/>
        </div>
        <div className="w-[140px]"><Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Status</Label>
          <Select value={filters.status || "all"} onValueChange={(v) => setFilters(f => ({ ...f, status: v === "all" ? "" : v }))}>
            <SelectTrigger data-testid="geo-filter-status" className="mt-1 rounded-sm bg-secondary border-border h-8 text-xs"><SelectValue/></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="on_time">On Time</SelectItem><SelectItem value="late">Late</SelectItem><SelectItem value="out_of_range">Out of Range</SelectItem></SelectContent>
          </Select>
        </div>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="geo-table">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">Date</th>
              <th className="text-left p-3">Employee</th>
              <th className="text-left p-3">Check-In</th>
              <th className="text-left p-3">Check-Out</th>
              <th className="text-left p-3">Location</th>
              <th className="text-right p-3">Distance</th>
              <th className="text-left p-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground" data-testid="geo-empty">No attendance entries</td></tr>}
            {pg.pageItems.map(r => (
              <tr key={r.id} data-testid={`geo-row-${r.id}`} className="border-t border-border">
                <td className="p-3 font-mono text-xs">{r.date}</td>
                <td className="p-3">{r.employee_name}</td>
                <td className="p-3 font-mono text-xs">{r.check_in ? new Date(r.check_in.time).toLocaleTimeString() : "—"}</td>
                <td className="p-3 font-mono text-xs">{r.check_out ? new Date(r.check_out.time).toLocaleTimeString() : "—"}</td>
                <td className="p-3 text-xs max-w-[280px] truncate" title={r.check_in?.address || ""}>
                  <div className="truncate">{addrOrCoords(r.check_in)}</div>
                  {r.check_in && <a data-testid={`geo-map-${r.id}`} href={locLink(r.check_in.lat, r.check_in.lng)} target="_blank" rel="noreferrer" className="text-[10px] text-primary hover:underline uppercase tracking-wider">View on Map</a>}
                </td>
                <td className="p-3 text-right font-mono text-xs">{r.distance_m ? `${Math.round(r.distance_m)}m` : "—"}</td>
                <td className={`p-3 text-xs font-semibold uppercase ${STATUS_COLORS[r.status] || ""}`}>{STATUS_LABEL[r.status] || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>

      <div className="text-[10px] text-muted-foreground mt-3 flex items-center gap-1"><CheckCircle2 className="w-3 h-3"/> Each employee can check in once and check out once per day. GPS is required.</div>
    </div>
  );
}
